// @ts-strict-ignore
import type { Services } from '~/flow/types';

import type {
  Action,
  LegacyThreshold,
  ObjectIdTransformationMap,
  ReasonCodeGroup,
  Threshold,
} from '../../types';
import { generatePublicId, getAttributeToken } from '../../utils';
import { ActionType, WorkflowTransformationIssueType } from '../../constants';
import type { FeatureFlags } from '~/helpers/typescript';
import { extractActionTypeFromId, isActionOfType } from 'flow/service/actions';

export function transformMLAAttributeComponents({ attributeFilterComponents }) {
  return attributeFilterComponents.map(filterComp => {
    const component = filterComp.attribute_component || filterComp;
    return {
      value: component.is_mock_mla_filter ? undefined : filterComp.value,
      attributeComponent: {
        attributeComponentId: component.attribute_component_id,
        precedence: component.precedence,
        placeholder: component.placeholder,
        dataType: component.data_type,
        label: component.label,
        isOptional: component.is_optional,
        possibleValues: component.possible_values,
        default: component.default,
      },
    };
  });
}

export function transformLegacyThresholdsToJsonRepresentation({
  thresholds,
  transformedActions,
  services,
  legacyActionsToTransformedActionsIdMap,
  reasonCodeGroups,
  featureFlags,
}: {
  thresholds: LegacyThreshold[];
  transformedActions: Record<string, Action>;
  services: Services;
  legacyActionsToTransformedActionsIdMap: ObjectIdTransformationMap;
  reasonCodeGroups: ReasonCodeGroup[];
  featureFlags: Partial<FeatureFlags>;
}) {
  const transformedJsonThresholds = {};
  const issuesWithTransformedJsonThresholds = [];
  const legacyToTransformedThresholdIdMap: Record<string, string> = {};

  thresholds.forEach(threshold => {
    const objectPublicId = threshold.publicId
      ? threshold.publicId : generatePublicId('threshold');
    const leftActionId = legacyActionsToTransformedActionsIdMap[threshold.leftActionId];
    const rightActionId = threshold.rightActionId
      ? legacyActionsToTransformedActionsIdMap[threshold.rightActionId]
      : null;
    const leftAction = transformedActions[leftActionId];
    const rightAction = rightActionId
      ? transformedActions[rightActionId]
      : null;

    const owningActionPublicId = legacyActionsToTransformedActionsIdMap[threshold.actionId];
    const owningActionType = extractActionTypeFromId(owningActionPublicId);

    const rightReasonCodeGroupId = threshold.rightCRGId;
    let matchingReasonCodeGroup: ReasonCodeGroup | null = null;
    if (rightReasonCodeGroupId) {
      matchingReasonCodeGroup = reasonCodeGroups.find(
        reasonCodeGroup => reasonCodeGroup.id === rightReasonCodeGroupId,
      );
    }
    const transformedThresholdObject: Threshold = {
      publicId: objectPublicId,
      actionId: owningActionPublicId,
      left: {
        actionId: leftActionId,
        attributeId: threshold.leftAttrId,
        attributeToken: getAttributeToken({
          action: leftAction,
          attributeId: threshold.leftAttrId,
          services,
        }),
        attributeComponents: transformMLAAttributeComponents({
          attributeFilterComponents: threshold.leftAttrComponents,
        }),
      },
      operator: threshold.operator,
      right: {
        actionId: rightActionId,
        attributeId: threshold.rightAttrId,
        attributeToken: getAttributeToken({
          action: rightAction,
          attributeId: threshold.rightAttrId,
          services,
        }),
        attributeComponents: transformMLAAttributeComponents({
          attributeFilterComponents: threshold.rightAttrComponents,
        }),
        reasonCodeGroupId: threshold.rightCRGId,
        reasonCodeGroup: matchingReasonCodeGroup || null,
        attributeValue: threshold.rightAttrValue,
        leftTagActionResult: threshold.rightTagActionSet,
      },
    };

    // Validate that matrix model actions are not used as right-hand side actions
    // This is a core business rule validation that should always run
    if (rightAction && isActionOfType(rightAction, [
      ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
    ])) {
      issuesWithTransformedJsonThresholds.push({
        publicId: objectPublicId,
        manualResolution: false,
        type: WorkflowTransformationIssueType.MATRIX_MODEL_NOT_SUPPORTED_AS_RHS_ACTION,
        data: transformedThresholdObject,
      });
    }

    if (featureFlags.workflowReviewIssuesAutoopen) {
      let issueType;
      if (
        leftAction?.type === ActionType.TAG
        && threshold.rightAttrValue === '0.0'
        && threshold.rightTagActionSet !== null
        // exit actions do not support editing, so no validation errors are built for them
        && owningActionType !== ActionType.EXIT
      ) {
        issueType = WorkflowTransformationIssueType
          .INCORRECT_THRESHOLD_RIGHT_ATTR_VALUE_LEFTACTION_TAG;
        transformedThresholdObject.right.attributeValue = null;
      }
      if (
        leftAction?.type === ActionType.OUTCOME_REASON
          && threshold.rightTagActionSet !== null
      ) {
        issueType = WorkflowTransformationIssueType
          .INCORRECT_THRESHOLD_RIGHT_ATTR_VALUE_LEFTACTION_OUTCOMEREASON;
        if (threshold.rightTagActionSet === true) {
          transformedThresholdObject.right.attributeValue = 'true';
        } else if (threshold.rightTagActionSet === false) {
          transformedThresholdObject.right.attributeValue = 'false';
        }
        transformedThresholdObject.right.leftTagActionResult = null;
      }

      if (issueType) {
        issuesWithTransformedJsonThresholds.push({
          publicId: objectPublicId,
          manualResolution: false,
          type: issueType,
          data: transformedThresholdObject,
        });
      }
    }

    transformedJsonThresholds[objectPublicId] = transformedThresholdObject;
    legacyToTransformedThresholdIdMap[threshold.actId] = objectPublicId;
  });

  return {
    transformedThresholds: transformedJsonThresholds,
    legacyThresholdsTransformationMap: legacyToTransformedThresholdIdMap,
    issuesWithTransformedJsonThresholds,
  };
}
